import { useState } from 'react';
import { useSelector } from 'react-redux';
import { Image, Spacer, Cell, Rating, Button, DeprecatedIcon } from '@vinted/web-ui';
import { noop } from 'lodash';
import { EMPTY_USER_IMAGE_PATH } from 'constants/images';
import { calculateRating } from 'data/utils/user';
import { scrollToElementById, reloadPage, navigateToPage } from 'libs/utils/window';
import { isDateInTheFuture } from 'libs/utils/date';
import { urlWithParams } from 'libs/utils/url';
import { blockUser, unblockUser } from 'data/api';
import { getUserId } from 'state/session/selectors';
import { getIsFeatureSwitchEnabled } from 'state/feature-switches/selectors';
import useBreakpoint from 'hooks/useBreakpoint';
import useLocation from 'hooks/useLocation';
import useTranslate from 'hooks/useTranslate';
import DropdownMenu from 'components/DropdownMenu';
import FollowButton from 'components/FollowButton';
import SeparatedList from 'components/SeparatedList';
import UserTitle from 'components/UserTitle';
import UserBlockModal from 'components/UserBlockModal';
import { NEW_ADMIN_ALERT_URL, PROFILE_SETTINGS_URL } from 'constants/routes';
import { NEW_MESSAGE_URL } from 'constants/routes/inbox';
import { AlertType } from 'constants/admin';
import UserInfo from './UserInfo';
import UserDescription from './UserDescription';
import UserStatuses from './UserStatuses';
import UnauthenticatedReportModal from '../UnauthenticatedReportModal';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";

const InfoHeader = _ref => {
  var _user$photo, _user$business_accoun;

  let {
    user,
    viewingSelf,
    showActions,
    showSupportFunctionality,
    canSendTemplates,
    screen,
    isAvailableForMigration,
    isShadowBanned,
    isContentTranslationEnabled,
    onTogglePoliciesModal = noop,
    onRatingClick = noop
  } = _ref;
  const breakpoints = useBreakpoint();
  const {
    relativeUrl
  } = useLocation();
  const translate = useTranslate('user');
  const isActionsVisible = showActions && !user.account_ban_date;
  const currentUserId = useSelector(getUserId);
  const isProfileBlockingImprovementsEnabled = useSelector(getIsFeatureSwitchEnabled('profile_blocking_improvements'));
  const isProfileReportingModalEnabled = useSelector(getIsFeatureSwitchEnabled('ntd_profile_reporting_modal'));
  const {
    0: isBlockModalVisible,
    1: setIsBlockModalVisible
  } = useState(false);
  const {
    0: isReportModalVisible,
    1: setIsReportModalVisible
  } = useState(false);
  if (breakpoints.phones) return null;
  const photoUrl = ((_user$photo = user.photo) === null || _user$photo === void 0 ? void 0 : _user$photo.url) || EMPTY_USER_IMAGE_PATH;
  const rating = calculateRating(user.feedback_reputation);

  const toggleBlock = async () => {
    if (!currentUserId) return;
    const toggleFunction = user.is_hated ? unblockUser : blockUser;
    await toggleFunction({
      currentUserId,
      blockedUserId: user.id
    });
    reloadPage();
  };

  function handleBlockCancel() {
    setIsBlockModalVisible(false);
  }

  function handleModalBlockButtonClick() {
    setIsBlockModalVisible(false);
    reloadPage();
  }

  function handleBlockClick() {
    if (isProfileBlockingImprovementsEnabled) {
      setIsBlockModalVisible(true);
      return;
    }

    toggleBlock();
  }

  function handleRatingsClick() {
    onRatingClick();
    scrollToElementById('tabs');
  }

  function handleReportClick() {
    if (currentUserId || !isProfileReportingModalEnabled) {
      const url = urlWithParams(NEW_ADMIN_ALERT_URL, {
        ref_id: user.id,
        ref_type: AlertType.Member,
        ref_url: relativeUrl
      });
      navigateToPage(url);
      return;
    }

    setIsReportModalVisible(true);
  }

  function handleReportModalClose() {
    setIsReportModalVisible(false);
  }

  function renderEditProfileAction() {
    if (!viewingSelf) return null;
    return /*#__PURE__*/_jsx(Button, {
      deprecatedIcon: /*#__PURE__*/_jsx(DeprecatedIcon, {
        name: "pencil",
        color: DeprecatedIcon.Color.GreyscaleLevel3
      }),
      url: PROFILE_SETTINGS_URL,
      text: translate('action.edit_profile'),
      theme: "muted",
      size: Button.Size.Medium,
      inline: true
    });
  }

  function renderMessageAction() {
    if (viewingSelf || !isActionsVisible || !user.allow_direct_messaging) return null;
    return /*#__PURE__*/_jsx(Button, {
      text: translate('action.write_message'),
      url: urlWithParams(NEW_MESSAGE_URL, {
        receiver_id: user.id,
        ref_url: relativeUrl
      }),
      size: Button.Size.Medium,
      testId: "info-header-message-button",
      inline: true
    });
  }

  function renderFollowAction() {
    if (viewingSelf || !isActionsVisible) return null;
    if (isDateInTheFuture(user.closet_countdown_end_date)) return null;
    return /*#__PURE__*/_jsx("div", {
      children: /*#__PURE__*/_jsx(FollowButton, {
        userId: user.id,
        isActive: user.is_favourite,
        size: Button.Size.Medium,
        testId: "profile-info-follow-button"
      })
    });
  }

  function renderExtraActions() {
    if (viewingSelf && !showSupportFunctionality) return null;
    const actions = [{
      id: 'report',
      text: translate('action.report'),
      onClick: handleReportClick
    }];

    if (currentUserId) {
      actions.push({
        id: user.is_hated ? 'unblock' : 'block',
        text: translate(user.is_hated ? 'action.unblock' : 'action.block'),
        onClick: handleBlockClick
      });
    }

    const dropdownButtonProps = {
      styling: Button.Styling.Flat,
      deprecatedIcon: /*#__PURE__*/_jsx(DeprecatedIcon, {
        testId: "actions-menu-button",
        name: "horizontal-dots",
        color: DeprecatedIcon.Color.GreyscaleLevel4,
        title: translate('a11y.show_more')
      }),
      size: Button.Size.Medium
    };
    return /*#__PURE__*/_jsx("div", {
      children: /*#__PURE__*/_jsx(DropdownMenu, {
        actions: actions,
        buttonProps: dropdownButtonProps
      })
    });
  }

  function renderMainActions() {
    return /*#__PURE__*/_jsx("div", {
      className: "u-flexbox",
      children: /*#__PURE__*/_jsxs(SeparatedList, {
        separator: /*#__PURE__*/_jsx(Spacer, {
          orientation: Spacer.Orientation.Vertical,
          size: Spacer.Size.Large
        }),
        children: [renderEditProfileAction(), renderMessageAction(), renderFollowAction()]
      })
    });
  }

  function renderAllActions() {
    return /*#__PURE__*/_jsx("div", {
      className: "u-flexbox",
      children: /*#__PURE__*/_jsxs(SeparatedList, {
        separator: /*#__PURE__*/_jsx(Spacer, {
          orientation: Spacer.Orientation.Vertical,
          size: Spacer.Size.Large
        }),
        children: [renderEditProfileAction(), renderMessageAction(), renderFollowAction(), renderExtraActions()]
      })
    });
  }

  function renderHeaderContent() {
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx("button", {
        type: "button",
        onClick: handleRatingsClick,
        "data-testid": "rating-button",
        children: /*#__PURE__*/_jsx(Rating, {
          aria: {
            'aria-label': rating && translate('a11y.rating', {
              rating,
              max_rating: 5
            }) || ''
          },
          value: rating,
          text: translate('feedback.reviews', {
            count: user.feedback_count
          }, {
            count: user.feedback_count
          }),
          emptyStateText: translate('feedback.no_reviews')
        })
      }), breakpoints.tablets && /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(Spacer, {}), renderMainActions()]
      })]
    });
  }

  function renderHeaderSuffix() {
    return breakpoints.desktops ? renderAllActions() : renderExtraActions();
  }

  function renderUserBlockModal() {
    return /*#__PURE__*/_jsx(UserBlockModal, {
      username: user.login,
      isUserBlocked: user.is_hated,
      userId: user.id,
      isVisible: isBlockModalVisible,
      onCancel: handleBlockCancel,
      onConfirm: handleModalBlockButtonClick
    });
  }

  return /*#__PURE__*/_jsxs("div", {
    className: "u-flexbox",
    children: [/*#__PURE__*/_jsx("div", {
      children: /*#__PURE__*/_jsx(Image, {
        src: photoUrl,
        styling: Image.Styling.Circle,
        size: Image.Size.X4Large,
        alt: ""
      })
    }), /*#__PURE__*/_jsx(Spacer, {
      size: Spacer.Size.Large,
      orientation: Spacer.Orientation.Vertical
    }), /*#__PURE__*/_jsxs("div", {
      className: "u-flex-grow",
      children: [/*#__PURE__*/_jsx(Cell, {
        title: /*#__PURE__*/_jsx(UserTitle, {
          businessAccountName: (_user$business_accoun = user.business_account) === null || _user$business_accoun === void 0 ? void 0 : _user$business_accoun.name,
          login: user.login,
          isBusiness: user.business
        }),
        body: renderHeaderContent(),
        suffix: renderHeaderSuffix()
      }), /*#__PURE__*/_jsx(UserStatuses, {
        onUnblockClick: isProfileBlockingImprovementsEnabled ? handleBlockClick : undefined,
        user: user,
        screen: screen,
        isAvailableForMigration: isAvailableForMigration,
        showSupportFunctionality: showSupportFunctionality,
        isShadowBanned: isShadowBanned,
        viewingSelf: viewingSelf
      }), /*#__PURE__*/_jsx(Cell, {
        children: /*#__PURE__*/_jsx(UserInfo, {
          user: user,
          showSupportFunctionality: showSupportFunctionality,
          canSendTemplates: canSendTemplates,
          onTogglePoliciesModal: onTogglePoliciesModal,
          viewingSelf: viewingSelf
        })
      }), /*#__PURE__*/_jsx(UserDescription, {
        user: user,
        isContentTranslationEnabled: isContentTranslationEnabled
      })]
    }), /*#__PURE__*/_jsx(UnauthenticatedReportModal, {
      isVisible: isReportModalVisible,
      onClose: handleReportModalClose
    }), renderUserBlockModal()]
  });
};

export default InfoHeader;